import { useState } from 'react';

import { useAdminUserSearchQuery } from '@/client/api/admin/users.query';
import useDebounce from '@/client/hooks/useDebounce';
import type { AdminUserSearchUser } from '@/client/types/admin/users-orgs';
import { Input } from '@/components/ui/input';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 150;

interface UserSearchSelectProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (user: AdminUserSearchUser) => void;
  placeholder?: string;
  className?: string;
}

export default function UserSearchSelect({
  value,
  onChange,
  onSelect,
  placeholder = 'Search users by name or email…',
  className,
}: UserSearchSelectProps) {
  const [open, setOpen] = useState(false);

  const debounced = useDebounce(value.trim(), DEBOUNCE_MS);
  const canSearch = debounced.length >= MIN_QUERY_LENGTH;

  const {
    data: users = [],
    isFetching,
    isError,
  } = useAdminUserSearchQuery(canSearch ? debounced : null, canSearch);

  const showDropdown = open && canSearch;

  const handleSelect = (user: AdminUserSearchUser) => {
    onSelect(user);
    setOpen(false);
  };

  return (
    <div className={`relative ${className ?? ''}`}>
      <Input
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      />

      {showDropdown && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border bg-white shadow">
          {isFetching && !users.length ? (
            <div className="px-3 py-2 text-sm text-gray-500">Searching…</div>
          ) : isError ? (
            <div className="px-3 py-2 text-sm text-red-600">
              Failed to search users. Try again.
            </div>
          ) : users.length ? (
            users.map((user) => (
              <button
                key={user.uaid}
                type="button"
                // Fire before the input's blur so the click isn't swallowed by
                // the dropdown closing.
                onMouseDown={(e) => e.preventDefault()}
                className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-gray-50"
                onClick={() => handleSelect(user)}
              >
                <span className="text-sm font-medium">
                  {user.first_name} {user.last_name}
                </span>
                <span className="text-xs text-gray-500">
                  {user.email} · uid {user.uid}
                </span>
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">No users found</div>
          )}
        </div>
      )}
    </div>
  );
}
